import { useEffect } from 'react';

/**
 * Back-handler registry.
 *
 * A module-level LIFO stack of close callbacks. Modals (and any other
 * dismissible overlay) push a `close` function on open and pop it on close.
 * The Android hardware back button — handled in `useNativeBackButton` — pops
 * the top of the stack first and falls through to route navigation only
 * when nothing is registered.
 *
 * Why a module singleton instead of a React context: the consumer is the
 * Capacitor `App.addListener('backButton', ...)` callback, which runs
 * outside React's render cycle and can't synchronously read context. A
 * plain module variable is simpler and equally correct for a per-app
 * (not per-tree) concern.
 *
 * Ordering: LIFO. The most recently opened overlay closes first, regardless
 * of CSS z-index. Multiple overlays open at once will close one per back
 * press until the stack is empty.
 *
 * Tradeoffs vs. the legacy DOM-scanning `closeTopOverlayIfPresent`:
 *  - No coupling to CSS class names or aria-labels.
 *  - Refactor-safe: renaming a class or moving close logic doesn't break it.
 *  - Deterministic ordering (most recently opened closes first).
 *  - Each overlay opts in explicitly — adding a new modal requires one line
 *    next to its `isOpen` state. The omission is visible at the source, not
 *    silently swallowed.
 */

type BackHandler = () => void;

const stack: BackHandler[] = [];

/**
 * Register a back handler. Returns a deregister function — call it on close
 * or unmount. Idempotent: registering the same function twice pushes twice,
 * and each deregister removes the most recent matching entry.
 */
export function pushBackHandler(close: BackHandler): () => void {
    stack.push(close);
    return () => {
        const i = stack.lastIndexOf(close);
        if (i >= 0) stack.splice(i, 1);
    };
}

/**
 * Pop and invoke the top handler. Returns true if a handler ran, false if
 * the stack was empty. Used by `useNativeBackButton` to determine whether
 * the back press was consumed by an overlay or should propagate to route
 * navigation.
 */
export function popBackHandler(): boolean {
    const fn = stack.pop();
    if (!fn) return false;
    try {
        fn();
    } catch {
        // A handler throwing must not poison the back button. Swallow and
        // continue; the user can press back again to fall through to nav.
    }
    return true;
}

/**
 * Number of registered handlers. Test/debug use only — production code
 * should not branch on this.
 */
export function backHandlerCount(): number {
    return stack.length;
}

/**
 * Clear all registered handlers. Test-only — production code should rely
 * on each handler's own deregister callback.
 */
export function __resetBackHandlerRegistry(): void {
    stack.length = 0;
}

/**
 * Register a back handler while `active` is true. On false or unmount, the
 * handler is deregistered. Idiomatic usage:
 *
 * ```tsx
 * const [isOpen, setIsOpen] = useState(false);
 * useBackHandler(() => setIsOpen(false), isOpen);
 * ```
 *
 * `close` is captured at the time `active` flips true. If you need the
 * handler to see the latest props/state, wrap it in `useCallback` with
 * proper deps — or just keep `close` simple (the setter form above is the
 * common case).
 */
export function useBackHandler(close: BackHandler, active: boolean): void {
    useEffect(() => {
        if (!active) return;
        return pushBackHandler(close);
    }, [close, active]);
}
